import React, { useEffect, useState } from 'react';
import {
  FlaskConical,
  FileText,
  Files,
  Plus,
  ArrowRight,
  FileQuestion,
  GraduationCap,
  Sparkles,
  ExternalLink,
  MoreVertical,
  Download,
  Copy,
  Trash2,
  Edit2,
  FileSpreadsheet,
} from 'lucide-react';
import { Button } from '../../shared/components/Button';
import { Card } from '../../shared/components/Card';
import { EmptyState } from '../../shared/components/EmptyState';
import { UNIVERSITY_CONFIG } from '../../config/university.config';
import { NavRoute } from '../../components/layout/Sidebar';
import { listAllProjects, loadProject, duplicateProject, deleteProject, renameProject, ProjectSummary } from '../../core/persistence/projectRepository';
import { useDocument } from '../../core/state/DocumentContext';
import { exportDocumentToPdf, downloadPdfBytes } from '../../core/export/pdfExporter';
import { exportDocumentToDocx, downloadDocxBlob } from '../../core/export/docxExporter';
import { useToast } from '../../shared/context/ToastContext';

interface DashboardProps {
  onNavigate: (route: NavRoute) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const { loadDocument, resetToSampleLabReport, newBlankDocument } = useDocument();
  const { showToast } = useToast();

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // Fetch projects from IndexedDB
  const refreshProjects = async () => {
    setLoading(true);
    try {
      const items = await listAllProjects();
      setProjects(items);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshProjects();
  }, []);

  const handleOpenProject = async (proj: ProjectSummary) => {
    const doc = await loadProject(proj.id);
    if (doc) {
      loadDocument(doc);
      if (proj.isCoverOnly) {
        onNavigate('cover-studio');
      } else {
        onNavigate('report-studio');
      }
    }
  };

  const handleCreateNewLabReport = () => {
    resetToSampleLabReport();
    onNavigate('report-studio');
  };

  const handleCreateNewCover = () => {
    newBlankDocument('Assignment Cover Page');
    onNavigate('cover-studio');
  };

  const handleDuplicate = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveMenuId(null);
    const cloned = await duplicateProject(id);
    if (cloned) {
      showToast('Document duplicated successfully.', 'success');
      refreshProjects();
    }
  };

  const handleRename = async (proj: ProjectSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveMenuId(null);
    const newTitle = window.prompt('Enter new document title:', proj.title);
    if (newTitle && newTitle.trim()) {
      await renameProject(proj.id, newTitle.trim());
      showToast('Document renamed.', 'info');
      refreshProjects();
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveMenuId(null);
    if (window.confirm('Are you sure you want to delete this document?')) {
      await deleteProject(id);
      showToast('Document deleted.', 'info');
      refreshProjects();
    }
  };

  const handleExportPdf = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveMenuId(null);
    const doc = await loadProject(id);
    if (doc) {
      try {
        const bytes = await exportDocumentToPdf(doc);
        downloadPdfBytes(bytes, `${doc.metadata.title || 'document'}.pdf`);
        showToast('PDF exported successfully.', 'success');
      } catch (err) {
        console.error('Export failed:', err);
        showToast('Failed to export PDF.', 'error');
      }
    }
  };

  const handleExportDocx = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveMenuId(null);
    const doc = await loadProject(id);
    if (doc) {
      try {
        const blob = await exportDocumentToDocx(doc);
        downloadDocxBlob(blob, `${doc.metadata.title || 'document'}.docx`);
        showToast('DOCX exported successfully.', 'success');
      } catch (err) {
        console.error('Export failed:', err);
        showToast('Failed to export DOCX.', 'error');
      }
    }
  };

  return (
    <div style={{ padding: 'var(--space-8)', display: 'flex', flexDirection: 'column', gap: 'var(--space-8)', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
      {/* Welcome Hero Banner */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--space-6)',
          paddingBottom: 'var(--space-4)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, marginBottom: 'var(--space-2)' }}>
            Student Document Studio
          </h1>
          <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)' }}>
            Create, edit, typeset, and organise publication-grade academic documents for your coursework and laboratory reports.
          </p>
        </div>

        {/* Institutional Badge */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-4)',
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-xs)',
          }}
        >
          <img
            src={UNIVERSITY_CONFIG.logoPath}
            alt="University Crest"
            style={{ width: 34, height: 34, objectFit: 'contain' }}
            onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
          />
          <div>
            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--text-primary)' }}>
              {UNIVERSITY_CONFIG.name}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              Configured Institutional Environment
            </div>
          </div>
          {UNIVERSITY_CONFIG.website && (
            <a
              href={UNIVERSITY_CONFIG.website}
              target="_blank"
              rel="noreferrer"
              className="icon-btn"
              style={{ width: 28, height: 28 }}
              title="Open University Website"
            >
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>

      {/* Quick Action Workspace Cards */}
      <div>
        <h2 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-4)' }}>
          Studio Workspaces
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--space-5)' }}>
          {/* Lab Report Studio Card */}
          <Card
            interactive
            onClick={() => onNavigate('report-studio')}
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 180 }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--primary-50)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary-600)' }}>
                  <FlaskConical size={22} />
                </div>
                <span className="badge badge-primary">Standard Report</span>
              </div>
              <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                Lab Report Studio
              </h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                Construct standard 9-section reports with theory, apparatus, KaTeX formulas, data tables, OCR text recognition, and live pagination.
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--primary-600)', marginTop: 'var(--space-4)' }}>
              <span>Launch Studio</span>
              <ArrowRight size={14} />
            </div>
          </Card>

          {/* Cover Page Studio Card */}
          <Card
            interactive
            onClick={() => onNavigate('cover-studio')}
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 180 }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-info-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-info)' }}>
                  <FileText size={22} />
                </div>
                <span className="badge badge-neutral">Standardized</span>
              </div>
              <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                Cover Page Studio
              </h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                Generate official department cover sheets with university crest, student IDs, teacher info, and custom academic styling.
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--primary-600)', marginTop: 'var(--space-4)' }}>
              <span>Create Cover Page</span>
              <ArrowRight size={14} />
            </div>
          </Card>

          {/* Document & Page Studio Card */}
          <Card
            interactive
            onClick={() => onNavigate('document-studio')}
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 180 }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-success)' }}>
                  <Files size={22} />
                </div>
                <span className="badge badge-neutral">In-Browser PDF</span>
              </div>
              <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                Document Studio
              </h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                Merge, split, rotate, reorder, and assemble generated reports with external PDF sheets without server uploads.
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--primary-600)', marginTop: 'var(--space-4)' }}>
              <span>Open PDF Studio</span>
              <ArrowRight size={14} />
            </div>
          </Card>
        </div>
      </div>

      {/* Recent Documents Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <h2 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600 }}>Saved Documents</h2>
            {projects.length > 0 && (
              <span className="badge badge-neutral">{projects.length}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button
              variant="secondary"
              size="sm"
              icon={<FileText size={14} />}
              onClick={handleCreateNewCover}
            >
              New Cover
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<Plus size={14} />}
              onClick={handleCreateNewLabReport}
            >
              New Lab Report
            </Button>
          </div>
        </div>

        {/* Project List */}
        {loading ? (
          <Card style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--text-muted)' }}>
            Loading saved documents...
          </Card>
        ) : projects.length === 0 ? (
          <Card style={{ padding: 'var(--space-8)' }}>
            <EmptyState
              icon={<FileQuestion size={28} />}
              title="No documents yet"
              description="Create your first academic document, laboratory report, or assignment cover page to get started."
              action={
                <Button
                  variant="primary"
                  icon={<Plus size={16} />}
                  onClick={handleCreateNewLabReport}
                >
                  Create Lab Report
                </Button>
              }
            />
          </Card>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 'var(--space-4)' }}>
            {projects.map((proj) => (
              <div
                key={proj.id}
                onClick={() => handleOpenProject(proj)}
                style={{
                  padding: 'var(--space-4) var(--space-5)',
                  backgroundColor: 'var(--bg-surface)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: 'var(--shadow-xs)',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 'var(--space-3)',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      {proj.isCoverOnly ? (
                        <FileText size={18} color="var(--primary-600)" />
                      ) : (
                        <FlaskConical size={18} color="var(--color-info)" />
                      )}
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {proj.documentType.replace('_', ' ')}
                      </span>
                    </div>

                    {/* Context Menu Button */}
                    <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ padding: '2px 6px', height: '24px' }}
                        onClick={() => setActiveMenuId(activeMenuId === proj.id ? null : proj.id)}
                        aria-label="Document options"
                      >
                        <MoreVertical size={14} />
                      </button>

                      {activeMenuId === proj.id && (
                        <div
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: '28px',
                            backgroundColor: 'var(--bg-surface)',
                            border: '1px solid var(--border-default)',
                            borderRadius: 'var(--radius-md)',
                            boxShadow: 'var(--shadow-lg)',
                            zIndex: 100,
                            minWidth: '160px',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: 'var(--space-1)',
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            style={{ justifyContent: 'flex-start', fontSize: 'var(--font-size-xs)' }}
                            onClick={(e) => handleRename(proj, e)}
                          >
                            <Edit2 size={13} style={{ marginRight: '6px' }} /> Rename
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            style={{ justifyContent: 'flex-start', fontSize: 'var(--font-size-xs)' }}
                            onClick={(e) => handleDuplicate(proj.id, e)}
                          >
                            <Copy size={13} style={{ marginRight: '6px' }} /> Duplicate
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            style={{ justifyContent: 'flex-start', fontSize: 'var(--font-size-xs)' }}
                            onClick={(e) => handleExportPdf(proj.id, e)}
                          >
                            <Download size={13} style={{ marginRight: '6px' }} /> Export PDF
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            style={{ justifyContent: 'flex-start', fontSize: 'var(--font-size-xs)' }}
                            onClick={(e) => handleExportDocx(proj.id, e)}
                          >
                            <FileSpreadsheet size={13} style={{ marginRight: '6px' }} /> Export DOCX
                          </button>
                          <div style={{ height: '1px', backgroundColor: 'var(--border-subtle)', margin: '4px 0' }} />
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            style={{ justifyContent: 'flex-start', fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
                            onClick={(e) => handleDelete(proj.id, e)}
                          >
                            <Trash2 size={13} style={{ marginRight: '6px' }} /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-1)' }}>
                    {proj.title}
                  </h3>

                  {proj.courseCode && (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--primary-600)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                      {proj.courseCode}
                    </div>
                  )}

                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {proj.studentName ? `Author: ${proj.studentName} • ` : ''}
                    {proj.sectionCount} {proj.sectionCount === 1 ? 'section' : 'sections'} • {proj.paperSize}
                  </p>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: 'var(--space-2)',
                    borderTop: '1px solid var(--border-subtle)',
                    fontSize: '11px',
                    color: 'var(--text-muted)',
                  }}
                >
                  <span>Modified {new Date(proj.modifiedAt).toLocaleDateString()}</span>
                  <span style={{ color: 'var(--primary-600)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '2px' }}>
                    Open <ArrowRight size={12} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Academic Highlights & Privacy Principles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-4)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', padding: 'var(--space-4)', backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
          <GraduationCap size={22} color="var(--primary-600)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>Standardized Academic Formatting</h4>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              Built strictly to IEEE, APA, and university submission standards with A4/Letter page metrics.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-3)', padding: 'var(--space-4)', backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
          <Sparkles size={22} color="var(--color-warning)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>KaTeX LaTeX Equation Support</h4>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              Render complex mathematical expressions and circuit formulas instantly with vector-accurate export.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
